// AppShell — app-level navigation that makes BOTH the chat interface and the
// 3D globe reachable in one app (#16). A slim bottom tab bar (iOS-style, thumb
// reachable on mobile and unambiguous as app-level nav) switches between the
// two surfaces; the active view renders in the flex-1 content area via <Outlet>.
//
// Layout contract: this is `flex flex-col h-screen`, so the content area has a
// concrete height and child pages fill it with `h-full` (ChatContainer/GlobeView
// no longer reference 100vh — they size to this container).
import { NavLink, Outlet } from 'react-router-dom'
import { MessageSquare, Globe2 } from 'lucide-react'

const TABS = [
  { to: '/chat', label: 'Chat', icon: MessageSquare, testid: 'tab-chat' },
  { to: '/globe', label: 'Globe', icon: Globe2, testid: 'tab-globe' },
] as const

export default function AppShell() {
  return (
    <div className="flex flex-col h-screen bg-retro-bg">
      {/* Active view */}
      <div className="flex-1 min-h-0">
        <Outlet />
      </div>

      {/* App-level tab bar */}
      <nav
        className="flex items-stretch border-t border-retro-border bg-retro-surface font-mono"
        aria-label="Primary"
        data-testid="app-tabbar"
      >
        {TABS.map(({ to, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            // /chat has sub-routes (/chat/:sessionId); treat any /chat* path as active.
            // Active accent (retro-amber) matches the chat header title. NavLink
            // sets aria-current="page" on the active tab automatically.
            className={({ isActive }) =>
              [
                'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[11px]',
                'uppercase tracking-wider transition-colors',
                isActive ? 'text-retro-amber' : 'text-retro-dim hover:text-retro-muted',
              ].join(' ')
            }
          >
            <Icon className="w-5 h-5" aria-hidden="true" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  )
}
